import config from '@/config';
import { jwtDecode } from 'jwt-decode';
import { authActions } from '@/store/reducers/auth';
import { gameActions } from '@/store/reducers/game';
import { AppDispatch } from '@/store';
import userUtils from '@/utils/user';
import { triggerForcedSessionLogout } from '@/utils/forcedSessionLogout';
import { errorActions } from '@/store/reducers/error';
import { uiActions } from '@/store/reducers/ui';
import i18nInstance from '@/locales/i18n';

interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token: string;
}

interface DecodedToken {
  'cognito:username': string;
  'custom:google_access_token': string;
  exp?: number;
}

const buildTokenExchangeUrl = (domain: string): string => {
  return `https://${domain}/oauth2/token`;
};

const buildTokenRequestBody = (code: string, clientId: string): URLSearchParams => {
  return new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code,
    redirect_uri: `${location.origin}/login`,
  });
};

interface TokenFetchError extends Error {
  status: number;
  cognitoError?: string;
}

const fetchTokens = async (url: string, body: URLSearchParams): Promise<TokenResponse> => {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!response.ok) {
    let cognitoError: string | undefined;
    try {
      const errorBody = (await response.json()) as Record<string, unknown>;
      if (typeof errorBody.error === 'string') {
        cognitoError = errorBody.error;
      }
    } catch {
      // ignore parse errors
    }
    const err = Object.assign(
      new Error(`Token exchange failed: ${response.status} ${response.statusText}`),
      { status: response.status, cognitoError }
    ) as TokenFetchError;
    throw err;
  }

  return response.json();
};

const handleTokenStorage = (
  decoded: DecodedToken,
  tokens: TokenResponse
): { userId: string; token: string } => {
  const cognitoUserPoolData = userUtils.getCognitoUserPoolData();

  const clientId = cognitoUserPoolData.awsCognitoClientId;
  const username = decoded['cognito:username'];

  localStorage.setItem(
    `CognitoIdentityServiceProvider.${clientId}.${username}.idToken`,
    tokens.id_token
  );
  localStorage.setItem(
    `CognitoIdentityServiceProvider.${clientId}.${username}.accessToken`,
    tokens.access_token
  );
  localStorage.setItem(
    `CognitoIdentityServiceProvider.${clientId}.${username}.refreshToken`,
    tokens.refresh_token
  );
  localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`, username);
  localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.clockDrift`, '0');

  if (tokens.refresh_token) {
    localStorage.setItem('refresh_token', tokens.refresh_token);
  }

  /*
  ELL-4949:
  You need to make sure that google_access_token exists and is not undefined or null, 
  otherwise it will store undefined/null as a string in localStorage 
  and this will cause errors when trying to access the server.
  */
  if (decoded['custom:google_access_token']) {
    localStorage.setItem('X-google-token', decoded['custom:google_access_token']);
  } else {
    localStorage.removeItem('X-google-token');
  }

  localStorage.removeItem('register-token');
  localStorage.removeItem('register-registration_data');

  const accessTokenDecoded = jwtDecode<{ user_id?: string }>(tokens.access_token);

  return {
    userId: accessTokenDecoded.user_id || decoded['cognito:username'],
    token: tokens.access_token,
  };
};

const buildRefreshTokenRequestBody = (refreshToken: string, clientId: string): URLSearchParams => {
  return new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: refreshToken,
  });
};

let showPopupOnCurrentFailure = false;
let connectionLostPopupShown = false;

const performRefresh = async (dispatch: AppDispatch): Promise<string> => {
  try {
    const cognitoUserPoolData = userUtils.getCognitoUserPoolData();

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const url = buildTokenExchangeUrl(cognitoUserPoolData.awsCognitoDomain);
    const body = buildRefreshTokenRequestBody(refreshToken, cognitoUserPoolData.awsCognitoClientId);
    const tokens = await fetchTokens(url, body);

    if (!tokens.id_token) {
      throw new Error('No ID token received during refresh');
    }

    const decoded = jwtDecode<DecodedToken>(tokens.id_token);
    const authData = handleTokenStorage(decoded, tokens);

    dispatch(authActions.setToken(authData.token));

    setupTokenRefresh(dispatch, decoded.exp);

    if (connectionLostPopupShown) {
      connectionLostPopupShown = false;
      dispatch(errorActions.hideError());
      dispatch(uiActions.hideErrorModal());
    }

    return authData.token;
  } catch (error) {
    console.error('Token refresh failed:', error);
    handleRefreshFailure(error, dispatch, showPopupOnCurrentFailure);
    return '';
  }
};

let inFlightRefresh: Promise<string> | null = null;

/**
 * `showPopupOnFailure` should only be set by callers reacting to a real, already-observed
 * request failure (e.g. the response interceptor retrying after a 401). Proactive/background
 * refresh attempts (request interceptors, idle-wake refresh) should omit it so a single
 * transient blip doesn't surface a disruptive popup before any user-facing request has
 * actually failed.
 */
export const refreshTokens = (
  dispatch: AppDispatch,
  { showPopupOnFailure = false }: { showPopupOnFailure?: boolean } = {}
): Promise<string> => {
  if (showPopupOnFailure) {
    showPopupOnCurrentFailure = true;
  }

  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  inFlightRefresh = performRefresh(dispatch).finally(() => {
    inFlightRefresh = null;
    showPopupOnCurrentFailure = false;
  });

  return inFlightRefresh;
};

/**
 * Cognito returns HTTP 400 + { error: "invalid_grant" } when the refresh token
 * has expired or been revoked. Only that case warrants a forced logout redirect.
 * Network errors, transient 5xx, or other failures should not send the user to
 * the Expired page — the response interceptors handle genuine auth failures.
 */
function isCognitoRefreshTokenExpiredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as Record<string, unknown>;

  if (e.cognitoError === 'invalid_grant') return true;

  const message = typeof e.message === 'string' ? e.message.toLowerCase() : '';
  return (
    message.includes('refresh token has expired') ||
    message.includes('token has been revoked') ||
    message.includes('invalid refresh token')
  );
}

const handleRefreshFailure = (
  error: unknown,
  dispatch: AppDispatch,
  showPopupOnFailure: boolean
): void => {
  if (isCognitoRefreshTokenExpiredError(error)) {
    triggerForcedSessionLogout();
    return;
  }

  if (!showPopupOnFailure) return;

  // Refresh failed for a reason other than a genuinely expired/revoked token
  // (e.g. a transient network/connection drop) — don't wipe the session or
  // show the "already expired" page. Show a recoverable popup instead.
  const popupPayload = {
    title: i18nInstance.t('error.connectionLost.title'),
    message: i18nInstance.t('error.connectionLost.description'),
    buttonText: i18nInstance.t('error.connectionLost.button'),
    navigateOnClose: 'refresh',
  };
  connectionLostPopupShown = true;
  dispatch(errorActions.showError(popupPayload));
  dispatch(uiActions.showErrorModal(popupPayload));
};

let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;

export const signIn = (dispatch: AppDispatch, token: string, userId: string): void => {
  dispatch(gameActions.clearAll());
  dispatch(
    authActions.signIn({
      token: token,
      userId: userId,
    })
  );

  if (token) {
    try {
      const decoded = jwtDecode(token);
      setupTokenRefresh(dispatch, decoded.exp);
    } catch (error) {
      console.error('Failed to set up token refresh:', error);
    }
  }
};

export const setupTokenRefresh = (dispatch: AppDispatch, expirationTime?: number): void => {
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
  }

  if (!expirationTime) {
    const cognitoUserPoolData = userUtils.getCognitoUserPoolData();

    const idToken = localStorage.getItem(
      `CognitoIdentityServiceProvider.${cognitoUserPoolData.awsCognitoClientId}.LastAuthUser`
    );
    if (idToken) {
      try {
        const decoded = jwtDecode<DecodedToken>(idToken);
        expirationTime = decoded.exp;
      } catch (error) {
        console.error('Failed to decode token:', error);
        return;
      }
    }
  }

  if (expirationTime) {
    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = expirationTime - now;

    const refreshDelay = Math.max(0, (timeUntilExpiry - 300) * 1000);

    refreshTimeoutId = setTimeout(() => {
      refreshTokens(dispatch);
    }, refreshDelay);
  }
};

const exchangeCodeForTokens = async (
  code: string,
  dispatch?: AppDispatch
): Promise<{ userId: string; token: string }> => {
  try {
    const cognitoUserPoolData = userUtils.getCognitoUserPoolData();

    const url = buildTokenExchangeUrl(cognitoUserPoolData.awsCognitoDomain);
    const body = buildTokenRequestBody(code, cognitoUserPoolData.awsCognitoClientId);
    const tokens = await fetchTokens(url, body);

    if (!tokens.id_token) {
      throw new Error('No ID token received');
    }

    const decoded = jwtDecode<DecodedToken>(tokens.id_token);
    const authData = handleTokenStorage(decoded, tokens);

    if (dispatch && decoded.exp) {
      setupTokenRefresh(dispatch, decoded.exp);
    }

    return authData;
  } catch (error) {
    console.error('Error exchanging code for tokens:', error);
    throw error;
  }
};

export default exchangeCodeForTokens;
